import React, { useState } from 'react';
import Clarity from '@microsoft/clarity';
import './FAQ.css';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggleItem = (id: string) => {
    const newOpenItems = new Set(openItems);
    const isOpening = !newOpenItems.has(id);
    
    if (newOpenItems.has(id)) {
      newOpenItems.delete(id);
    } else {
      newOpenItems.add(id);
    }
    
    // Find the question text for the event
    const sectionIndex = parseInt(id.split('-')[0]);
    const itemIndex = parseInt(id.split('-')[1]);
    const question = faqSections[sectionIndex]?.items[itemIndex]?.question;
    
    Clarity.event(isOpening ? 'faq_question_open' : 'faq_question_close');
    
    setOpenItems(newOpenItems);
  };

  const faqSections: FAQSection[] = [
    {
      title: "About The Experience",
      items: [
        {
          question: "What is this service?",
          answer: "We offer a unique way to capture the candid, unscripted moments of your family's life. A personal storyteller, our \"Memory Keeper,\" spends a few days in your home, artfully documenting the giggles, cuddles, and firsts you'll want to cherish forever. You get to live in the moment, and we'll handle the memories."
        },
        {
          question: "How will I receive my photos?",
          answer: "After your rental period ends, we'll send you a link to a private, secure online gallery. From there, you can view all your beautiful photos, download your favorites, and share them with family and friends."
        },
        {
          question: "How many photos will I get?",
          answer: "Each of our \"Keepsake Collections\" comes with a set number of professionally edited, cinematic photos. You will also receive access to all the other wonderful, unedited candid shots captured during the rental period."
        },
        {
          question: "Can I give input on the style of photos?",
          answer: "Absolutely! We offer a special add-on that allows you to share your vision and preferences with us, so we can tailor the final edited images to your family's unique style."
        }
      ]
    },
    {
      title: "About Your Memory Keeper",
      items: [
        {
          question: "What exactly is the \"Memory Keeper\"?",
          answer: "Think of it as a quiet, little artist that lives with you for a few days. It's a small, four-legged robot designed to find the best angles and light to capture beautiful, candid photos from a respectful distance. Its focus is entirely on the memories, not the technology."
        },
        {
          question: "Is it safe to have around my baby and my family?",
          answer: "Your family's safety is our highest priority. The Memory Keeper is designed to be incredibly safe. It always maintains a significant distance from your baby and family members and operates with advanced safety sensors."
        },
        {
          question: "Does the Memory Keeper record audio?",
          answer: "No, never. To protect your family's privacy, the device is designed to capture only visual moments and does not have any audio recording capabilities."
        }
      ]
    },
    {
      title: "Booking and Logistics",
      items: [
        {
          question: "How does the rental process work?",
          answer: "It's simple! Just choose an available week on our calendar and fill out a quick request form. A member of our team will then get in touch with you to confirm the details and finalize your booking."
        },
        {
          question: "How is the device delivered and set up?",
          answer: "We deliver the Memory Keeper right to your doorstep. It comes with a quick, 5-minute self-setup guide that is incredibly easy to follow."
        },
        {
          question: "What if I need to cancel or reschedule my booking?",
          answer: "We understand that life with little ones can be unpredictable. Please contact us as soon as possible, and we will do our best to accommodate your needs."
        }
      ]
    },
    {
      title: "Privacy and Data",
      items: [
        {
          question: "How is my family's privacy protected?",
          answer: "We take your privacy very seriously. All photos are immediately synced and stored on our secure, encrypted servers. Your private gallery is accessible only to you, and we have strict policies in place to ensure your memories remain yours alone."
        },
        {
          question: "Can I delete my photos?",
          answer: "Yes, you have complete control. From your private gallery, you can decide which photos to keep and which to permanently delete. Once a photo is deleted, it is gone forever."
        }
      ]
    }
  ];

  return (
    <div className="faq-page">
      <div className="faq-header">
        <h1>Frequently Asked Questions</h1>
        <p>Everything you need to know about our memory-keeping service</p>
      </div>
      
      <div className="faq-content">
        {faqSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="faq-section">
            <h2 className="section-title">{section.title}</h2>
            <div className="faq-items">
              {section.items.map((item, itemIndex) => {
                const itemId = `${sectionIndex}-${itemIndex}`;
                const isOpen = openItems.has(itemId);
                
                return (
                  <div key={itemId} className="faq-item">
                    <button 
                      className={`faq-question ${isOpen ? 'open' : ''}`}
                      onClick={() => toggleItem(itemId)}
                      aria-expanded={isOpen}
                    >
                      <span className="question-text">{item.question}</span>
                      <span className="toggle-icon">{isOpen ? '−' : '+'}</span>
                    </button>
                    <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
                      <div className="answer-content">
                        <p>{item.answer}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      
      <div className="faq-footer">
        <p>Still have questions? <a 
          href="mailto:support@poppy.com"
          onClick={() => Clarity.event('faq_contact_email')}
        >Contact us</a></p>
        <a 
          href="/" 
          className="back-link"
          onClick={() => Clarity.event('faq_back_to_home')}
        >← Back to Home</a>
      </div>
    </div>
  );
};

export default FAQ;